import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Mic } from 'lucide-react';
import { Card } from '../atoms/Card/Card';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox';
import { Input } from '../atoms/Input/Input';
import { Textarea } from '../atoms/Textarea/Textarea';
import { Select } from '../atoms/Select/Select';
import { Alert } from '../atoms/Alert/Alert';
import { Inline } from '../atoms/Inline';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { OverflowMenu } from '../atoms/OverflowMenu';
import { ConfirmDialog } from '../ui/ConfirmDialog/ConfirmDialog';
import { Dialog } from '../ui/Dialog/Dialog';
import { AiFeedbackControl } from '../aiFeedback/AiFeedbackControl';
import { useTranslation } from '../../i18n/useTranslation';
import { useAquariums } from '../../hooks/useAquariums';
import { captureKeys, useCaptureMutations } from '../../hooks/useCaptures';
import { ApiError } from '../../lib/apiClient';
import type {
  ApplyItem,
  CaptureDto,
  CaptureSuggestionDto,
  JournalEntryPayload,
  ResultFeedbackDto,
  SuggestionPayload,
} from '../../types/capture';
import { SuggestionRow } from './SuggestionRow';
import { parameterLabel } from '../measurements/measurementHistoryUtils';
import { TranscriptPanel, reanalyseHint } from './TranscriptPanel';
import {
  COMPACT_KINDS,
  RECORD_KINDS,
  TASK_KINDS,
  formatDateTime,
  formatNumber,
  openSuggestions,
} from './suggestionFormat';
import './CaptureReviewCard.css';

/** The app languages a note can be re-analysed in (L5). */
export const NOTE_LANGUAGES = ['de', 'en', 'fr', 'es', 'it', 'nl', 'ja'] as const;

export interface CaptureReviewCardProps {
  capture: CaptureDto;
  /** After a successful save: how many open suggestions remain on this capture. */
  onSaved?: (captureId: string, remaining: number) => void;
  /** After the note was discarded. */
  onDiscarded?: (captureId: string) => void;
}

/** Id of a card's heading — the review page moves focus there after a save. */
export function captureCardHeadingId(captureId: string): string {
  return `capture-card-heading-${captureId}`;
}

/** Compact card (persona review): only measurements, dosing, maintenance, water changes, completions. */
export function isCompactCapture(capture: CaptureDto): boolean {
  const open = openSuggestions(capture.suggestions);
  return open.length > 0 && open.every((s) => COMPACT_KINDS.has(s.kind));
}

function initialSelection(suggestions: readonly CaptureSuggestionDto[]): Record<string, boolean> {
  const selection: Record<string, boolean> = {};
  for (const s of suggestions) selection[s.id] = !s.needsReview;
  return selection;
}

function feedbackText(item: ResultFeedbackDto, t: ReturnType<typeof useTranslation>['t'], language: string): string[] {
  const value = formatNumber(item.value, language);
  const range = {
    parameter: parameterLabel(t, item.parameterId, item.parameterName),
    value,
    unit: item.unit,
    min: item.targetMin !== null ? formatNumber(item.targetMin, language) : '',
    max: item.targetMax !== null ? formatNumber(item.targetMax, language) : '',
  };
  const lines: string[] = [];
  switch (item.kind) {
    case 'below_target':
      lines.push(t('voice.feedback.belowTarget', range));
      break;
    case 'above_target':
      lines.push(t('voice.feedback.aboveTarget', range));
      break;
    case 'in_range':
      lines.push(t('voice.feedback.inRange', range));
      break;
    case 'stale':
      lines.push(t('voice.feedback.stale', { parameter: parameterLabel(t, item.parameterId, item.parameterName), count: item.daysSinceLast ?? 0 }));
      break;
    case 'no_previous':
      lines.push(t('voice.feedback.noPrevious', { parameter: parameterLabel(t, item.parameterId, item.parameterName) }));
      break;
    default:
      break;
  }
  if (item.kind !== 'stale' && item.daysSinceLast !== null && item.daysSinceLast > 0) {
    lines.push(t('voice.feedback.lastTest', { count: item.daysSinceLast }));
  }
  if (item.nextStepKey) lines.push(t(item.nextStepKey));
  return lines;
}

/**
 * One capture as a selection list (wireframes "Capture Review Card" and
 * "Compact"): every suggestion is a checkbox row — confident ones start
 * checked, "Please check" ones unchecked — and "Save N selected" saves the
 * checked rows in one atomic step. Unchecked rows stay for later.
 */
export function CaptureReviewCard({ capture, onSaved, onDiscarded }: CaptureReviewCardProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const queryClient = useQueryClient();
  const { data: aquariums } = useAquariums();
  const { apply, reanalyse, discard } = useCaptureMutations();
  const tasksPanelId = useId();
  const saveHintId = useId();
  const saveErrorRef = useRef<HTMLDivElement>(null);

  const open = useMemo(() => openSuggestions(capture.suggestions), [capture.suggestions]);
  const entrySuggestion = open.find((s) => s.kind === 'journal_entry') ?? null;
  const records = open.filter((s) => RECORD_KINDS.has(s.kind));
  const tasks = open.filter((s) => TASK_KINDS.has(s.kind));
  const compactCapable = isCompactCapture(capture);
  /** "Text only, no AI": no suggestions to rate; "Analyse with AI" upgrades it. */
  const textOnly = capture.analysisMode === 'transcript';

  const [expanded, setExpanded] = useState(!compactCapable);
  const [selection, setSelection] = useState<Record<string, boolean>>(() => initialSelection(open));
  const [edits, setEdits] = useState<Record<string, SuggestionPayload>>({});
  const [tasksOpen, setTasksOpen] = useState(() => tasks.some((s) => s.needsReview));
  const [aquariumId, setAquariumId] = useState(capture.detectedAquariumId ?? capture.aquariumId);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<'failed' | 'conflict' | null>(null);
  const [reanalyseError, setReanalyseError] = useState<'failed' | 'limit' | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [languageDialog, setLanguageDialog] = useState(false);
  const [noteLanguage, setNoteLanguage] = useState(capture.language);

  // New suggestions (re-analysis, partial save) reset the defaults of rows that are new.
  const suggestionKey = open.map((s) => s.id).join(',');
  useEffect(() => {
    setSelection((current) => {
      const next = initialSelection(open);
      for (const id of Object.keys(next)) if (id in current) next[id] = current[id]!;
      return next;
    });
    setEdits((current) => Object.fromEntries(Object.entries(current).filter(([id]) => open.some((s) => s.id === id))));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the open suggestion ids
  }, [suggestionKey]);

  useEffect(() => {
    if (saveError) saveErrorRef.current?.focus();
  }, [saveError]);

  const payloadOf = (s: CaptureSuggestionDto): SuggestionPayload => edits[s.id] ?? s.payload;
  const setChecked = (id: string, checked: boolean): void => setSelection((cur) => ({ ...cur, [id]: checked }));
  const setPayload = (id: string, payload: SuggestionPayload): void => {
    setEdits((cur) => ({ ...cur, [id]: payload }));
    setChecked(id, true);
  };

  const selectedIds = open.filter((s) => selection[s.id]).map((s) => s.id);
  const selectedCount = selectedIds.length;
  const hasMultipleTanks = (aquariums?.length ?? 0) >= 2;
  const focused = open.find((s) => s.id === focusedId) ?? null;
  const highlight =
    focused && focused.spanStart !== null && focused.spanEnd !== null ? { start: focused.spanStart, end: focused.spanEnd } : null;
  const busy = apply.isPending || reanalyse.isPending || discard.isPending;

  const save = (): void => {
    if (selectedCount === 0) return;
    setSaveError(null);
    const items: ApplyItem[] = selectedIds.map((id) => {
      const edited = edits[id];
      return edited ? { id, payload: edited as unknown as Record<string, unknown> } : { id };
    });
    apply.mutate(
      {
        id: capture.id,
        request: { aquariumId, items },
      },
      {
        onSuccess: (result) => {
          const remaining = openSuggestions(result.capture.suggestions).length;
          setAnnouncement(t('voice.card.announceSaved', { count: items.length }));
          onSaved?.(capture.id, remaining);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            setSaveError('conflict');
            void queryClient.invalidateQueries({ queryKey: captureKeys.all });
          } else {
            setSaveError('failed');
          }
        },
      },
    );
  };

  const runReanalyse = (transcript?: string, languageCode?: string): void => {
    setReanalyseError(null);
    reanalyse.mutate(
      { id: capture.id, transcript, language: languageCode },
      {
        onError: (err) => {
          setReanalyseError(err instanceof ApiError && err.code === 'AI_BUDGET_EXCEEDED' ? 'limit' : 'failed');
        },
      },
    );
  };

  const title = t('voice.card.title', { dateTime: formatDateTime(capture.recordedAt, language) });
  const leftAfterPartialSave = capture.savedCount > 0 ? open.length : 0;

  const renderRow = (s: CaptureSuggestionDto, compact = false) => (
    <li key={s.id} className="capture-review-card__row">
      <SuggestionRow
        suggestion={s}
        payload={payloadOf(s)}
        checked={selection[s.id] ?? false}
        onCheckedChange={(checked) => setChecked(s.id, checked)}
        onPayloadChange={(payload) => setPayload(s.id, payload)}
        onFocusRow={(row) => setFocusedId(row.id)}
        compact={compact}
        disabled={busy}
        aquariumId={aquariumId}
      />
    </li>
  );

  const saveBlock = (
    <div className="capture-review-card__save">
      {saveError && (
        <Alert ref={saveErrorRef} tabIndex={-1} tone="danger" title={t('voice.card.saveFailedTitle')} data-testid="capture-save-error">
          {saveError === 'conflict' ? t('voice.card.saveConflict') : t('voice.card.saveFailedBody')}
        </Alert>
      )}
      <Button
        variant="primary"
        size="lg"
        fullWidth={!expanded}
        isLoading={apply.isPending}
        disabled={selectedCount === 0 || (busy && !apply.isPending)}
        aria-describedby={selectedCount === 0 ? saveHintId : undefined}
        onClick={save}
        data-testid="capture-save"
      >
        {t('voice.card.saveSelected', { count: selectedCount })}
      </Button>
      {selectedCount === 0 && (
        <Text variant="caption" color="muted" id={saveHintId}>
          {t('voice.card.nothingSelected')}
        </Text>
      )}
    </div>
  );

  const heading = (
    <Heading level={3} id={captureCardHeadingId(capture.id)} tabIndex={-1} className="capture-review-card__title">
      {title}
    </Heading>
  );

  const liveRegion = (
    <LiveRegion visuallyHidden>{announcement}</LiveRegion>
  );

  if (!expanded) {
    return (
      <Card padding="md" className="capture-review-card capture-review-card--compact" data-testid={`capture-card-${capture.id}`}>
        <div className="capture-review-card__header">
          {heading}
          <Button variant="ghost" size="sm" aria-expanded={false} onClick={() => setExpanded(true)}>
            {t('voice.card.showMore')}
          </Button>
        </div>
        {leftAfterPartialSave > 0 && (
          <Text variant="caption" color="muted">{t('voice.card.itemsLeft', { count: leftAfterPartialSave })}</Text>
        )}
        <ul className="capture-review-card__list">{open.map((s) => renderRow(s, true))}</ul>
        {saveBlock}
        {liveRegion}
      </Card>
    );
  }

  const feedbackItems = capture.feedback.filter((f) => open.some((s) => s.id === f.suggestionId));
  const entryPayload = entrySuggestion ? (payloadOf(entrySuggestion) as JournalEntryPayload) : null;

  return (
    <Card padding="lg" className="capture-review-card" data-testid={`capture-card-${capture.id}`}>
      <div className="capture-review-card__header">
        <span className="capture-review-card__title-row">
          <Mic size={18} aria-hidden="true" className="capture-review-card__icon" />
          {heading}
        </span>
        <span className="capture-review-card__header-actions">
          {compactCapable && (
            <Button variant="ghost" size="sm" aria-expanded onClick={() => setExpanded(false)}>
              {t('voice.card.showLess')}
            </Button>
          )}
          <OverflowMenu
            label={t('voice.card.more')}
            items={[
              { id: 'language', label: t('voice.card.language'), onSelect: () => setLanguageDialog(true) },
              { id: 'discard', label: t('voice.card.discardAll'), tone: 'danger', onSelect: () => setConfirmDiscard(true) },
            ]}
          />
        </span>
      </div>

      {capture.emergencySituation && (
        <Alert
          tone="warning"
          compact
          live="off"
          title={t('voice.card.emergencyTitle')}
          action={
            <Link
              className="capture-review-card__link"
              to={`/aquariums/${encodeURIComponent(capture.aquariumId)}/emergency?situation=${encodeURIComponent(capture.emergencySituation)}`}
            >
              {t('voice.card.emergencyOpen')}
            </Link>
          }
        >
          {t('voice.card.emergencyBody')}
        </Alert>
      )}

      {leftAfterPartialSave > 0 && (
        <Text variant="caption" color="muted">{t('voice.card.itemsLeft', { count: leftAfterPartialSave })}</Text>
      )}

      {hasMultipleTanks && (
        <div className="capture-review-card__tank">
          <Select
            label={t('voice.card.tank')}
            value={aquariumId}
            disabled={busy}
            onChange={(e) => setAquariumId(e.target.value)}
            options={(aquariums ?? []).map((a) => ({ value: a.id, label: a.name }))}
          />
          {capture.detectedAquariumId && capture.detectedPhrase && (
            <Text variant="caption" color="muted">{t('voice.card.tankDetected', { phrase: capture.detectedPhrase })}</Text>
          )}
        </div>
      )}

      {feedbackItems.length > 0 && (
        <Alert
          tone="info"
          live="off"
          compact
          title={t('voice.feedback.title')}
          action={
            <Link className="capture-review-card__link" to={`/aquariums/${encodeURIComponent(aquariumId)}/parameter-targets`}>
              {t('voice.feedback.learnMore')}
            </Link>
          }
        >
          {feedbackItems.flatMap((item) => feedbackText(item, t, language)).join(' ')}
        </Alert>
      )}

      {capture.transcript !== null && (
        <TranscriptPanel
          transcript={capture.transcript}
          highlight={highlight}
          reanalysisCredits={capture.reanalysisCredits}
          isReanalysing={reanalyse.isPending}
          disabled={busy && !reanalyse.isPending}
          upgrade={textOnly}
          onReanalyse={(text) => runReanalyse(text === capture.transcript ? undefined : text)}
        />
      )}
      {textOnly && (
        <div className="capture-review-card__text-only" data-testid="capture-text-only-note">
          <Text size="sm" color="secondary">{t('voice.card.textOnlyNote')}</Text>
          <Inline gap="2">
            <Button
              variant="secondary"
              size="sm"
              isLoading={reanalyse.isPending}
              disabled={busy && !reanalyse.isPending}
              onClick={() => runReanalyse(undefined)}
            >
              {t('voice.card.analyseWithAi')}
            </Button>
            <Text as="span" variant="caption" color="muted">
              {reanalyseHint(t, capture.reanalysisCredits, true)}
            </Text>
          </Inline>
        </div>
      )}
      {reanalyseError && (
        <Alert tone={reanalyseError === 'limit' ? 'warning' : 'danger'} compact>
          {reanalyseError === 'limit' ? t('voice.card.reanalyseLimit') : t('voice.card.reanalyseFailed')}
        </Alert>
      )}

      {entrySuggestion && entryPayload && (
        <section className="capture-review-card__section" aria-labelledby={`${capture.id}-entry`}>
          <Heading level={4} id={`${capture.id}-entry`} className="capture-review-card__section-title">
            {t('voice.card.section.entry')}
          </Heading>
          <Checkbox
            label={t('voice.card.saveEntry')}
            checked={selection[entrySuggestion.id] ?? false}
            disabled={busy}
            onChange={(e) => setChecked(entrySuggestion.id, e.target.checked)}
          />
          <Input
            label={t('voice.card.entryTitle')}
            value={entryPayload.title}
            maxLength={255}
            disabled={busy}
            onFocus={() => setFocusedId(entrySuggestion.id)}
            onChange={(e) => setPayload(entrySuggestion.id, { ...entryPayload, title: e.target.value })}
          />
          <Textarea
            label={t('voice.card.entryText')}
            value={entryPayload.text}
            rows={5}
            disabled={busy}
            onFocus={() => setFocusedId(entrySuggestion.id)}
            onChange={(e) => setPayload(entrySuggestion.id, { ...entryPayload, text: e.target.value })}
          />
        </section>
      )}

      {records.length > 0 && (
        <section className="capture-review-card__section" aria-labelledby={`${capture.id}-records`}>
          <Heading level={4} id={`${capture.id}-records`} className="capture-review-card__section-title">
            {t('voice.card.section.records')}
          </Heading>
          <ul className="capture-review-card__list">{records.map((s) => renderRow(s))}</ul>
        </section>
      )}

      {tasks.length > 0 && (
        <div className="capture-review-card__section">
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={tasksOpen}
            aria-controls={tasksPanelId}
            onClick={() => setTasksOpen((v) => !v)}
          >
            {t('voice.card.section.tasksCollapsed', { count: tasks.length })}
          </Button>
          <ul id={tasksPanelId} hidden={!tasksOpen} className="capture-review-card__list">
            {tasks.map((s) => renderRow(s))}
          </ul>
        </div>
      )}

      {saveBlock}

      {!textOnly && (
        <AiFeedbackControl
          outputType="voice_capture"
          outputId={capture.id}
          aquariumId={capture.aquariumId}
          labels={{
            prompt: t('voice.card.feedbackQuestion'),
            up: t('voice.card.feedbackUp'),
            down: t('voice.card.feedbackDown'),
            commentLabel: t('voice.card.feedbackComment'),
            submit: t('voice.card.feedbackSend'),
          }}
        />
      )}
      {liveRegion}

      <ConfirmDialog
        isOpen={confirmDiscard}
        title={t('voice.card.discardTitle')}
        message={t('voice.card.discardBody')}
        confirmLabel={t('voice.card.discardAll')}
        cancelLabel={t('common.cancel')}
        isConfirming={discard.isPending}
        onCancel={() => setConfirmDiscard(false)}
        onConfirm={() =>
          discard.mutate(capture.id, {
            onSettled: () => setConfirmDiscard(false),
            onSuccess: () => onDiscarded?.(capture.id),
          })
        }
      />
      <Dialog
        isOpen={languageDialog}
        onClose={() => setLanguageDialog(false)}
        title={t('voice.card.languageTitle')}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setLanguageDialog(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              onClick={() => {
                setLanguageDialog(false);
                runReanalyse(undefined, noteLanguage);
              }}
            >
              {t('voice.card.languageConfirm')}
            </Button>
          </>
        }
      >
        <Select
          label={t('voice.card.languageLabel')}
          value={noteLanguage}
          onChange={(e) => setNoteLanguage(e.target.value)}
          options={NOTE_LANGUAGES.map((code) => ({ value: code, label: languageName(code, language) }))}
        />
        <Text variant="caption" color="muted">
          {reanalyseHint(t, capture.reanalysisCredits, textOnly)}
        </Text>
      </Dialog>
    </Card>
  );
}

function languageName(code: string, uiLanguage: string): string {
  try {
    return new Intl.DisplayNames([uiLanguage], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}
